import { IntegrationConnection } from "@cohesive/api-client";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  createCommand,
  KEY_DOWN_COMMAND,
  LexicalCommand,
  TextNode,
} from "lexical";
import { useCallback, useEffect, useRef } from "react";
import { INSERT_TEXT_COMMAND } from "../TextReplacePlugin/TextReplacePlugin";
import {
  $createConnectionNode,
  ConnectionNode,
  ConnectionPayload,
} from "./ConnectionNode";

export const INSERT_CONNECTION_COMMAND: LexicalCommand<ConnectionPayload> =
  createCommand();

export const TRIGGER_CONNECTION_MENU_COMMAND: LexicalCommand<{
  position: number;
}> = createCommand();

type ConnectionPluginProps = {
  enabled?: boolean;
  connections: IntegrationConnection[];
  enableTrigger?: boolean;
};

export function ConnectionPlugin({
  connections,
  enabled,
  enableTrigger = true,
}: ConnectionPluginProps): null {
  const [editor] = useLexicalComposerContext();
  const connectionsRef = useRef(connections);

  // Keep ref updated with latest connections
  useEffect(() => {
    connectionsRef.current = connections;
  }, [connections]);

  useEffect(() => {
    if (!editor.hasNodes([ConnectionNode])) {
      throw new Error("ConnectionNode not registered on editor");
    }
  }, [editor]);

  const textTransform = useCallback((textNode: TextNode) => {
    const textContent = textNode.getTextContent();

    // Regex to match {connection:platform:type:id:title} format
    const match = /\{connection:([^}]*):([^}]*):([^}]*):([^}]*)\}/i.exec(
      textContent,
    );

    if (!match) return;

    const [, platform, type, id, name] = match;

    const platformMatch = connectionsRef.current.find(
      (p) => p.integration_platform === platform,
    );

    if (!platformMatch) return;

    const connectionNode = $createConnectionNode({
      platform: platform || "",
      type: type || "",
      identifier: id || "",
      title: name || "",
    });

    // If full text content matches, we can replace existing textNode
    // otherwise we need to split up the text node based on match index
    if (match[0] === textContent) {
      textNode.replace(connectionNode);
      return;
    }

    const startIndex = match.index || 0;
    const endIndex = startIndex + match[0].length;

    if (startIndex === 0) {
      const [currentNode] = textNode.splitText(endIndex);
      currentNode?.replace(connectionNode);
    } else {
      const [, currentNode] = textNode.splitText(startIndex, endIndex);
      currentNode?.replace(connectionNode);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    return editor.registerNodeTransform(TextNode, textTransform);
  }, [editor, enabled, textTransform]);

  useEffect(() => {
    if (!enabled) return;

    const insertConnectionNode = (payload: ConnectionPayload) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const connectionNode = $createConnectionNode(payload);

          // Get the anchor node (where cursor is)
          const anchorNode = selection.anchor.getNode();

          // Check if it's a text node and ends with @
          if (anchorNode instanceof TextNode) {
            const text = anchorNode.getTextContent();
            if (text.endsWith("@")) {
              // Remove the trailing @ by updating text content
              anchorNode.setTextContent(text.slice(0, -1));
            }
          }

          selection.insertNodes([connectionNode, $createTextNode(" ")]);
        }
      });

      return true;
    };

    // Registering custom command that will be handled by this plugin
    return editor.registerCommand(
      INSERT_CONNECTION_COMMAND,
      insertConnectionNode,
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor, enabled]);

  // Register @ keypress detection to trigger connection menu
  useEffect(() => {
    if (!enabled || !enableTrigger) return;

    const handleKeyDown = (e: KeyboardEvent): boolean => {
      if (e.key === "@") {
        let shouldTrigger = false;

        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            const anchorOffset = selection.anchor.offset;

            // Check if we're in a text node
            if (anchorNode instanceof TextNode) {
              const textContent = anchorNode.getTextContent();
              const charBeforeCursor = textContent[anchorOffset - 1];

              // Only trigger if there's a space/newline before @ (or cursor is at start of node)
              shouldTrigger =
                !charBeforeCursor ||
                charBeforeCursor === " " ||
                charBeforeCursor === "\n";
            } else {
              // If not in a text node (e.g., empty editor), trigger
              shouldTrigger = true;
            }
          }
        });

        if (shouldTrigger) {
          const fullTextContent = editor.getRootElement()?.textContent || "";
          e.preventDefault();
          editor.dispatchCommand(INSERT_TEXT_COMMAND, { text: "@" });
          editor.dispatchCommand(TRIGGER_CONNECTION_MENU_COMMAND, {
            position: fullTextContent.length,
          });
          return true;
        }
      }

      return false;
    };

    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      handleKeyDown,
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor, enabled, enableTrigger]);

  return null;
}
